import { useEffect, useRef, useState } from 'react';
import { useRunningTimers, setTimers, fmtClock, useChipPrefs } from '@/lib/plannerTimer';
import { fetchPlannerDocs, savePlannerDocs } from '@/integrations/plannerApi';

/** Minimal shape of a day doc — enough to pause/switch/add tasks from
 *  outside the view. Extra fields ride along untouched (spread on write). */
interface DayTask {
  id: string; title?: string; done?: boolean; spent?: number; running?: boolean;
  startAt?: number | null; segs?: { s: number; e: number }[];
}
interface DayDoc { tasks?: DayTask[] }

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * The floating stopwatch — the planner's running timer follows the user to
 * every screen of the portal. Ticks real seconds; clicking it returns to the
 * planner; the pause button stops the clock RIGHT HERE, and the ⇆ button
 * jumps to another of today's tasks (pause one, start the other) — both by
 * patching the day documents directly, no need to open the planner.
 * Past 4 hours it turns amber and asks "¿sigues en esto?" — but it NEVER
 * pauses by itself; that decision is the owner's.
 */
export function PlannerTimerChip({ inPlanner, defaultOn, onOpen }: {
  /** The planner section is on screen (the view shows its own clocks). */
  inPlanner?: boolean;
  /** Role default: Recurring ON, super admins OFF — Ajustes overrides it. */
  defaultOn?: boolean;
  onOpen: () => void;
}) {
  const timers = useRunningTimers();
  const [, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [sw, setSw] = useState<DayTask[] | null>(null); // today's switch list, when open
  const [adding, setAdding] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [closedSig, setClosedSig] = useState(() => { try { return sessionStorage.getItem('pk-chip-closed') || ''; } catch { return ''; } });
  const boxRef = useRef<HTMLDivElement | null>(null);

  const prefs = useChipPrefs();
  const mode = prefs.mode ?? (defaultOn ? 'on' : 'off');
  const corner = prefs.corner || 'br';
  /* ✕ closes for THIS session and THIS set of timers — a new ▶ brings it back. */
  const sig = timers.length ? timers.map((t) => `${t.taskId}:${t.startAt}`).join('|') : 'idle';
  const close = (e: React.MouseEvent) => {
    e.stopPropagation();
    try { sessionStorage.setItem('pk-chip-closed', sig); } catch { /* ignore */ }
    setClosedSig(sig);
  };

  useEffect(() => {
    if (!timers.length) return;
    const h = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(h);
  }, [timers.length]);

  /* Close the popovers on outside click / Escape. */
  useEffect(() => {
    if (sw == null && !adding) return;
    const click = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) { setSw(null); setAdding(false); } };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { setSw(null); setAdding(false); } };
    document.addEventListener('mousedown', click);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', click); document.removeEventListener('keydown', key); };
  }, [sw, adding]);

  if (mode === 'off') return null;
  if (inPlanner && !prefs.inPlanner) return null;
  if (closedSig === sig) return null;

  /** Pause every running task, and optionally start another one of today's. */
  const pauseAnd = async (startId: string | null) => {
    if (busy) return;
    setBusy(true);
    try {
      const docs = await fetchPlannerDocs();
      const stop = Date.now();
      const patch: Record<string, unknown> = {};
      for (const tm of timers) {
        const k = `day:${tm.day}`;
        const day = docs[k] as DayDoc | undefined;
        const task = day?.tasks?.find((x) => x.id === tm.taskId);
        if (day && task && task.running) {
          task.spent = (task.spent || 0) + (stop - (task.startAt || stop)) / 1000;
          if (task.startAt) task.segs = [...(task.segs || []), { s: task.startAt, e: stop }].slice(-30);
          task.running = false; task.startAt = null;
          patch[k] = day;
        }
      }
      let started: { day: string; task: DayTask } | null = null;
      if (startId) {
        const k = `day:${todayIso()}`;
        const day = (patch[k] || docs[k]) as DayDoc | undefined;
        const task = day?.tasks?.find((x) => x.id === startId);
        if (day && task && !task.done) {
          task.running = true; task.startAt = stop;
          patch[k] = day;
          started = { day: todayIso(), task };
        }
      }
      if (Object.keys(patch).length) await savePlannerDocs(patch);
      setTimers(started
        ? [{ day: started.day, taskId: started.task.id, title: started.task.title || '', startAt: stop, spent: started.task.spent || 0 }]
        : []);
      setSw(null);
    } catch { /* network hiccup — keep the chip so nothing is lost */ }
    finally { setBusy(false); }
  };

  const openSwitch = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    if (sw != null) { setSw(null); return; }
    setBusy(true);
    try {
      const docs = await fetchPlannerDocs();
      const day = docs[`day:${todayIso()}`] as DayDoc | undefined;
      const running = new Set(timers.map((x) => x.taskId));
      setSw((day?.tasks || []).filter((x) => !x.done && !running.has(x.id)));
    } catch { setSw([]); }
    finally { setBusy(false); }
  };

  /* ── The idle LAUNCHER: nothing running → a discreet dot that captures a
     task on the fly ("+") or starts one of today's ("▶") from any screen. */
  const quickAdd = async () => {
    const title = addTitle.trim();
    if (!title || busy) return;
    setBusy(true);
    try {
      const docs = await fetchPlannerDocs();
      const k = `day:${todayIso()}`;
      const day = (docs[k] as DayDoc | undefined) || {};
      day.tasks = [...(day.tasks || []), {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        title, done: false, spent: 0, running: false, startAt: null,
      }];
      await savePlannerDocs({ [k]: day });
      setAddTitle(''); setAdding(false);
    } catch { /* keep the text so nothing typed is lost */ }
    finally { setBusy(false); }
  };

  if (!timers.length) {
    return (
      <div className={`pk-float-box c-${corner}`} ref={boxRef}>
        {adding && (
          <div className="pk-float-sw">
            <div className="pk-float-sw-cap">Apuntar tarea para hoy</div>
            <div className="pk-float-add">
              <input
                autoFocus type="text" value={addTitle} placeholder="¿Qué te acaban de pedir?"
                onChange={(e) => setAddTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void quickAdd(); if (e.key === 'Escape') setAdding(false); }}
              />
              <button type="button" onClick={() => void quickAdd()}>{busy ? '…' : 'Añadir'}</button>
            </div>
          </div>
        )}
        {sw != null && !adding && (
          <div className="pk-float-sw">
            <div className="pk-float-sw-cap">Arrancar cronómetro</div>
            {sw.length
              ? sw.map((x) => (
                <button key={x.id} type="button" className="pk-float-sw-it" onClick={() => void pauseAnd(x.id)}>
                  ▶ {x.title || '(sin título)'}
                </button>
              ))
              : <div className="pk-float-sw-empty">No hay tareas pendientes hoy — apunta una con ＋.</div>}
          </div>
        )}
        <div className="pk-float pk-float-idle">
          <span
            className="pk-float-pause" role="button" aria-label="Apuntar tarea"
            title="Apuntar una tarea para hoy sin abrir el planificador"
            onClick={() => { setSw(null); setAdding(!adding); }}
          >＋</span>
          <span
            className="pk-float-pause" role="button" aria-label="Arrancar cronómetro"
            title="Arrancar el cronómetro de una tarea de hoy"
            onClick={(e) => { setAdding(false); void openSwitch(e); }}
          >▶</span>
          <span className="pk-float-open" role="button" title="Abrir el planificador" onClick={onOpen}>Planner</span>
          <span className="pk-float-x" role="button" aria-label="Cerrar" title="Cerrar (vuelve al arrancar un cronómetro; desactívala del todo en Ajustes del planner)" onClick={close}>✕</span>
        </div>
      </div>
    );
  }

  const t = timers[0];
  const sec = (t.spent || 0) + (Date.now() - t.startAt) / 1000;
  const forgotten = sec > 4 * 3600;
  return (
    <div className={`pk-float-box c-${corner}`} ref={boxRef}>
      {sw != null && (
        <div className="pk-float-sw">
          <div className="pk-float-sw-cap">Cambiar a otra tarea de hoy</div>
          {sw.length
            ? sw.map((x) => (
              <button key={x.id} type="button" className="pk-float-sw-it" onClick={() => void pauseAnd(x.id)}>
                ▶ {x.title || '(sin título)'}
              </button>
            ))
            : <div className="pk-float-sw-empty">No hay más tareas pendientes hoy — créalas en el planificador.</div>}
        </div>
      )}
      <button className={`pk-float ${forgotten ? 'forgot' : ''}`} onClick={onOpen} title={forgotten ? `¿Sigues en «${t.title}»? Lleva más de 4 h — cámbiala o páusala` : `${t.title} — abrir el planificador`}>
        <span className="pk-float-dot" aria-hidden />
        <span className="pk-float-time">{fmtClock(sec)}</span>
        <span className="pk-float-title">{forgotten ? `¿Sigues en esto? · ${t.title}` : t.title}</span>
        {timers.length > 1 && <span className="pk-float-more">+{timers.length - 1}</span>}
        <span
          className={`pk-float-pause ${busy ? 'busy' : ''}`}
          role="button"
          aria-label="Cambiar de tarea"
          title="Cambiar a otra tarea de hoy"
          onClick={(e) => void openSwitch(e)}
        >
          ⇆
        </span>
        <span
          className={`pk-float-pause ${busy ? 'busy' : ''}`}
          role="button"
          aria-label="Pausar"
          title="Pausar el cronómetro"
          onClick={(e) => { e.stopPropagation(); void pauseAnd(null); }}
        >
          {busy ? '…' : '⏸'}
        </span>
        <span className="pk-float-x" role="button" aria-label="Cerrar" title="Cerrar la burbuja (el cronómetro SIGUE corriendo; vuelve al arrancar otro)" onClick={close}>✕</span>
      </button>
    </div>
  );
}
